import { useState, forwardRef } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";
import { IoSearch } from "react-icons/io5";

import { FilterPillBar } from "../FilterPillBar";
import { Thumbnails } from "../Thumbnails";
import { EmbeddablePlaylistViewModel, TagItem } from "../../ViewModel";
import EmbeddablePlayer from "../../../EmbeddablePlayer";
import { NoClips } from "../NoClips";
import { Button } from "MVVM/Components/Button";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  PlayerPhotoState,
  TeamLogoState,
  FallbackPlaylistTypeState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
  RenderPreMatchState,
  TopViewedPlayersState,
  TopViewedMatchesState,
  TopViewedTeamsState,
  DisplayEmptyState,
  TopViewedPlayersByCountryState,
} from "MVVM/State/embeddablePlaylist";
import { analytics } from "MVVM/Analytics";
import { TopViewedListContainer } from "../TopViewedListContainer";
import { EmptyState } from "./Components/EmptyState";
import { HLTVSurvey } from "../HLTVSurvey";

export const HLTVEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    theme?: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, theme = "night", handleMouseEnter }, ref) => {
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playerPhoto = useRecoilValue(PlayerPhotoState);
  const teamLogo = useRecoilValue(TeamLogoState);
  const displayEmptyState = useRecoilValue(DisplayEmptyState);
  const fallbackPlaylistType = useRecoilValue(FallbackPlaylistTypeState);
  const renderPreMatchState = useRecoilValue(RenderPreMatchState);
  const topViewedPlayers = useRecoilValue(TopViewedPlayersState);
  const topViewedPlayersByCountry = useRecoilValue(
    TopViewedPlayersByCountryState,
  );
  const topViewedMatches = useRecoilValue(TopViewedMatchesState);
  const topViewedTeams = useRecoilValue(TopViewedTeamsState);
  const renderTopViewedComponent = !!topViewedPlayers && !!topViewedMatches;
  const renderTopViewedList = new URLSearchParams(window.location.search).get(
    "topViewedList",
  );

  const showProfileCTA = true;

  const handlePlayerPortraitClick = () => {
    if (!showProfileCTA || !activePlaylistClip?.playerGameIdentifier) return;

    analytics.track("Button - Click", {
      location: "embeddable_playlist",
      locationIdentifier: "player_portrait_profile_cta",
      platform,
      playerGameIdentifier: activePlaylistClip.playerGameIdentifier,
      username: activePlaylistClip.username,
    });

    const domain = window.location.origin;
    const profileUrl = `${domain}/u/${
      activePlaylistClip.playerGameIdentifier
    }?utm_source=${platform}&ajs_aid=${window.rudderanalytics.getAnonymousId()}`;

    window.open(profileUrl, "_blank");
  };

  const { filters, getClipsForTag } = EmbeddablePlaylistViewModel({
    playlist,
    platform,
  });

  const filterPills = filters.map((tag: TagItem) => ({
    label: tag.value,
    key: `${tag.key}:${tag.value}`,
    count: tag.clips.length,
  }));

  const allFilters = [
    { label: "All", key: "all", count: playlistClips?.length || 0 },
    ...filterPills,
    { label: "More", key: "more", count: 0, externalLink: true },
  ];

  const clipsToDisplay = getClipsForTag(selectedTag);

  if (displayEmptyState)
    return (
      <Flex flexDirection="column" width="100%">
        <StyledContainer
          $renderTopViewedComponent={true}
          $renderPreMatchState={renderPreMatchState}
        >
          <StyledGradientLayer
            className="gradient-layer"
            $renderTopViewedComponent={true}
          >
            <EmptyState />
          </StyledGradientLayer>
          {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
            <Box style={{ height: "150px" }}>
              <LoadingSkeleton height="100%" width="100%" />
            </Box>
          ) : (
            <TopViewedListContainer
              topViewedPlayers={topViewedPlayers}
              topViewedPlayersByCountry={topViewedPlayersByCountry}
              topViewedMatches={topViewedMatches}
              topViewedTeams={topViewedTeams}
              theme={theme}
            />
          )}
        </StyledContainer>
      </Flex>
    );
  return (
    <Flex flexDirection={"column"} style={{ height: "100%", width: "100%" }}>
      <StyledContainer
        $renderTopViewedComponent={renderTopViewedComponent}
        $renderPreMatchState={renderPreMatchState}
        onMouseEnter={handleMouseEnter}
      >
        <StyledGradientLayer
          className="gradient-layer"
          $renderTopViewedComponent={false}
        >
          <StyledPlayerColumn>
            <InnerRow>
              <LeftCell>
                <FluidPlayerWrapper ref={ref} className="fixed-width-container">
                  {activePlaylistClip && (
                    <EmbeddablePlayer
                      userRole="viewer"
                      onPlayEndVariant="nextUpPlaylist"
                    />
                  )}
                  {fallbackPlaylistType &&
                    !["lastMatch", "teamClips"].includes(
                      fallbackPlaylistType,
                    ) && <NoClips />}
                  {!activePlaylistClip && !fallbackPlaylistType && (
                    <PlayerLoadingSkeleton>
                      <LoadingSkeleton width="100%" height="100%" />
                    </PlayerLoadingSkeleton>
                  )}
                </FluidPlayerWrapper>
              </LeftCell>

              {renderPreMatchState ? (
                <StyledPreMatchContainer>
                  <StyledLogo
                    src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/allstarLogoGradient.svg`}
                  />
                  <StyledPreMatchText>
                    See your own clips while waiting for this match.
                  </StyledPreMatchText>
                  <StyledPreMatchButton
                    onClick={() => {
                      const platform = new URLSearchParams(
                        window.location.search,
                      ).get("platform");
                      const ajs_aid = window.rudderanalytics.getAnonymousId();
                      window.open(
                        `https://allstar.gg/match-history?ajs_aid=${ajs_aid}&utm_source=${platform}`,
                        "_blank",
                      );
                    }}
                  >
                    Click Here
                  </StyledPreMatchButton>
                </StyledPreMatchContainer>
              ) : (
                <StyledImageContainer>
                  {teamLogo && <StyledTeamImage src={teamLogo} />}
                  {playerPhoto && (
                    <>
                      <StyledPlayerImage
                        src={playerPhoto}
                        onClick={handlePlayerPortraitClick}
                        style={{
                          cursor: showProfileCTA ? "pointer" : "default",
                        }}
                      />
                      {showProfileCTA &&
                        activePlaylistClip?.username &&
                        (() => {
                          const teamName = activePlaylistClip.metadata?.find(
                            (m) => m.key === "teamName",
                          )?.value;

                          return (
                            <PlayerInfoContainer
                              onClick={handlePlayerPortraitClick}
                              title={`Visit ${activePlaylistClip.username}'s Profile`}
                            >
                              <PlayerNameText>
                                {activePlaylistClip.username}
                                <LinkIcon />
                              </PlayerNameText>
                              {teamName && (
                                <TeamNameText>{teamName}</TeamNameText>
                              )}
                            </PlayerInfoContainer>
                          );
                        })()}
                    </>
                  )}
                </StyledImageContainer>
              )}
            </InnerRow>
          </StyledPlayerColumn>
          {renderPreMatchState && renderTopViewedList && (
            <MinHeightCol>
              <Thumbnails clipsToDisplay={clipsToDisplay} />
            </MinHeightCol>
          )}
          <NoFlexCol>
            {renderPreMatchState ? (
              !topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
                <StyledLoadingContainer>
                  <LoadingSkeleton height="100%" width="100%" />
                </StyledLoadingContainer>
              ) : (
                <TopViewedListContainer
                  topViewedPlayers={topViewedPlayers}
                  topViewedPlayersByCountry={topViewedPlayersByCountry}
                  topViewedMatches={topViewedMatches}
                  topViewedTeams={topViewedTeams}
                  theme={theme}
                />
              )
            ) : (
              <>
                {allFilters.length > 0 ? (
                  <FilterPillBar
                    filters={allFilters}
                    selected={selectedTag}
                    onSelect={setSelectedTag}
                  />
                ) : (
                  <FilterPillBarSkeleton>
                    <Flex style={{ gap: "8px" }}>
                      {Array.from({ length: 5 }, (_, i) => (
                        <LoadingSkeleton
                          key={i}
                          width={48}
                          height={22}
                          style={{ borderRadius: "20px" }}
                        />
                      ))}
                    </Flex>
                  </FilterPillBarSkeleton>
                )}
              </>
            )}
          </NoFlexCol>
          {!renderPreMatchState && (
            <ThumbnailsWrapper>
              <Thumbnails clipsToDisplay={clipsToDisplay} />
              <HLTVSurvey platform={platform} playlist={playlist} />
            </ThumbnailsWrapper>
          )}

          {renderTopViewedList && !renderPreMatchState && (
            <StyledTopViewedColumn>
              {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
                <StyledLoadingContainer>
                  <LoadingSkeleton height="100%" width="100%" />
                </StyledLoadingContainer>
              ) : (
                <TopViewedListContainer
                  topViewedPlayers={topViewedPlayers}
                  topViewedPlayersByCountry={topViewedPlayersByCountry}
                  topViewedMatches={topViewedMatches}
                  topViewedTeams={topViewedTeams}
                  theme={theme}
                />
              )}
            </StyledTopViewedColumn>
          )}
        </StyledGradientLayer>
      </StyledContainer>
    </Flex>
  );
});

const StyledTeamImage = styled(Image)`
  position: absolute;
  opacity: 75%;
  right: -5px;
  top: 24px;
  width: 150px;

  @media (max-width: 515px) {
    width: 128px;
    top: 0px;
  }

  @media (max-width: 380px) {
    right: 0px;
    width: 86px;
  }
`;

const StyledPlayerImage = styled(Image)`
  position: absolute;
  right: -5px;
  top: 88px;
  width: 150px;

  -webkit-mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;

  @media (max-width: 515px) {
    top: 48px;
  }

  @media (max-width: 425px) {
    top: 50px;
  }

  @media (max-width: 380px) {
    right: -2px;
    top: 35px;
    width: 115px;
  }
`;

const PlayerInfoContainer = styled(Box)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: absolute;
  right: -5px;
  text-align: center;
  top: 238px;
  width: 150px;

  @media (max-width: 515px) {
    top: 178px;
    width: 128px;
  }

  @media (max-width: 425px) {
    right: 0px;
    top: 168px;
  }

  @media (max-width: 380px) {
    left: -10px;
    right: 0px;
    top: 130px;
    width: 115px;
  }
`;

const PlayerNameText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6));
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 16px;
  text-align: center;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.8),
    0 0 8px rgba(255, 255, 255, 0.3);

  ${PlayerInfoContainer}:hover & {
    color: ${({ theme }) => theme.colors.envy};
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.8),
      0 0 12px rgba(77, 189, 148, 0.4);
  }

  @media (max-width: 515px) {
    font-size: 11px;
    line-height: 14px;
  }

  @media (max-width: 425px) {
    font-size: 10px;
    line-height: 12px;
  }

  @media (max-width: 380px) {
    font-size: 9px;
    line-height: 11px;
  }
`;

const TeamNameText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  font-size: 11px;
  font-weight: 500;
  line-height: 13px;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);

  @media (max-width: 515px) {
    font-size: 9px;
    line-height: 11px;
  }

  @media (max-width: 425px) {
    font-size: 8px;
    line-height: 10px;
  }

  @media (max-width: 380px) {
    font-size: 7px;
    line-height: 9px;
  }
`;

const FilterPillBarSkeleton = styled(Box)``;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledLogo = styled(Image)`
  height: 60px;
  width: 34px;

  @media (max-width: 515px) {
    height: 43px;
    width: 24px;
  }

  @media (max-width: 380px) {
    height: 32px;
    width: 18px;
  }
`;

const StyledPreMatchText = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;

  @media (max-width: 502px) {
    font-size: 13px;
    line-height: 15px;
  }

  @media (max-width: 380px) {
    font-size: 10px;
    line-height: 12px;
  }
`;

const StyledPreMatchContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  margin: 0 auto;
  width: 185px;

  @media (max-width: 502px) {
    gap: 10px;
    width: 115px;
    padding-left: 5px;
  }
`;

const StyledPreMatchButton = styled(Button)`
  background: linear-gradient(225deg, #f95370 0%, #72439a 100%);
  border-radius: 100px;
  font-size: 15px;
  font-weight: 700;
  height: 2.5rem;
  padding: 0px;
  line-height: 2.5rem;
  width: 115px;

  @media (max-width: 502px) {
    font-size: 10px;
    height: 1.5rem;
    line-height: 1.5rem;
  }

  @media (max-width: 380px) {
    font-size: 8px;
    height: 1rem;
    line-height: 1rem;
    width: 60px;
  }
`;

const StyledLoadingContainer = styled(Box)`
  height: 180px;

  @media (max-width: 502px) {
    height: 150px;
  }

  @media (max-width: 380px) {
    height: 120px;
  }
`;

const StyledGradientLayer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
}>`
  backdrop-filter: blur(6px);
  background: linear-gradient(
      227.35deg,
      rgba(49, 113, 138, 0.3) -3.85%,
      rgba(99, 173, 146, 0.3) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.99), rgba(0, 0, 0, 0.99));
  flex-direction: column;
  gap: 12px;
  height: 100%;
  padding: 10px;
  width: 100%;

  min-height: 0;
  flex: 1;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
    flex: 1;
  `}

  @media (max-width: 515px) {
    gap: 8px;
    padding: 8px;
  }

  @media (max-width: 425px) {
    padding: 6px;
  }
`;

const StyledContainer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
  $renderPreMatchState: boolean;
}>`
  background-image: url(${process.env
    .REACT_APP_S3_CLIP_BUCKET}/static/web/playlistBackgroundImage.jpg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-direction: column;
  width: 100%;
  height: 100vh;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
      flex: 1;
      min-height: 0;
      gap: 0px;
    `}
`;

const FluidPlayerWrapper = styled(Box)`
  border-radius: 8px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;
  width: 100%;

  .video-js {
    width: 100% !important;
    height: 100% !important;
    background: transparent;
  }
  .vjs-tech {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
  }
`;

const StyledImageContainer = styled(Box)`
  flex: 0 0 auto;
  min-width: 150px;
  position: relative;

  @media (max-width: 515px) {
    min-width: 128px;
  }

  @media (max-width: 425px) {
    min-width: 100px;
  }

  @media (max-width: 470px) {
    min-width: 90px;
  }
`;

const LeftCell = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
`;

const StyledPlayerColumn = styled(Box)`
  align-items: stretch;
  display: flex;
  flex: 3 1 0%;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const InnerRow = styled(Flex)`
  display: flex;
  flex: 1;
  gap: 0px;
  min-height: 0;
  min-width: 0;
  width: 100%;
`;

const MinHeightCol = styled(StyledPlayerColumn)`
  flex: 2 1 0%;
  min-height: 0px;
`;

const ThumbnailsWrapper = styled(MinHeightCol)`
  position: relative;
`;

const NoFlexCol = styled(StyledPlayerColumn)`
  flex: 0 0 auto;
`;

const StyledTopViewedColumn = styled(Box)`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  gap: 12px;
  height: 100%;
`;

const LinkIcon = styled(IoSearch)`
  display: inline-block;
  font-size: 0.9em;
  margin-left: 5px;
  opacity: 0.8;
  position: relative;
  top: 1px;

  ${PlayerInfoContainer}:hover & {
    opacity: 1;
  }

  @media (max-width: 515px) {
    font-size: 0.85em;
  }

  @media (max-width: 380px) {
    font-size: 0.8em;
    margin-left: 3px;
  }
`;
